/**
 * Talking to the API, and remembering the device token once paired.
 *
 * Every request is to a **same-origin** `/v1` path. The app contains no host
 * name at all, which is deliberate: the product has no domain yet, and this
 * way that decision blocks nothing. In development a Vite proxy puts the API
 * on this origin; in staging and production a reverse proxy does. On a Quest
 * over `adb reverse tcp:5173` the app and the API arrive together on
 * localhost, which also happens to be a secure context, so WebXR works with no
 * certificate.
 *
 * The day a domain exists, nothing here changes.
 */

import { isDeviceRevokedAnswer } from '@vrlearning/lesson-schema';
import { forgetSchoolData } from './school-data.js';
import { forgetTrouble, rememberTrouble, troubleToReport } from './trouble.js';

const TOKEN_KEY = 'vrlearning.device.v1';

export type DeviceIdentity = {
  readonly deviceToken: string;
  readonly pairedAt: string;
};

export type PairingProgress =
  | { status: 'code'; code: string; expiresAt: Date }
  | { status: 'paired'; identity: DeviceIdentity }
  | { status: 'expired' }
  | { status: 'error'; message: string };

/**
 * The device token, if this headset has been paired.
 *
 * localStorage rather than a cookie: the token is used from script, never sent
 * automatically, and a headset in a cupboard must still be paired next term.
 */
export function storedIdentity(): DeviceIdentity | undefined {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as DeviceIdentity;
    return typeof parsed.deviceToken === 'string' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function storeIdentity(identity: DeviceIdentity): void {
  // A new pairing can end again (revoked), even under a token seen before.
  revocations.delete(identity.deviceToken);
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(identity));
  } catch {
    // Private mode or a full quota. The lesson can still run; the headset will
    // simply have to pair again next time.
  }
}

/**
 * Ends the pairing on this headset: the token, and with it everything the
 * school left here -- its lessons, its photos, its queued results
 * (school-data.ts). Resolves once all of it is gone.
 *
 * The token goes first: should the page die halfway, the headset is
 * unpaired, and pairing again clears the rest before it stores anything.
 */
export async function forgetIdentity(): Promise<void> {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing to do; see storeIdentity.
  }
  await forgetSchoolData();
}

// --- the pairing ending while the headset runs ------------------------------

/**
 * Thrown by deviceFetch when the API said the token is no headset's: the
 * headset has been unpaired by the time it is caught, and the caller has
 * nothing left to do -- above all, not draw over the pairing screen.
 */
export class DeviceRevokedError extends Error {
  readonly revoked = true;
  constructor() {
    super('this headset was unlinked from its school');
    this.name = 'DeviceRevokedError';
  }
}

/**
 * Whether an answer is the API saying no headset holds this token (docs/05):
 * a 401 whose body says `device-revoked`. Nothing else is: not a bare 401 (a
 * captive portal, a proxy, an older API), not a 403, not a 5xx, not an
 * expired signed media URL, not a network failure. A properly paired headset
 * must never be wiped by any of those. Reads a copy, so the caller can still
 * read the body.
 */
export async function saysRevoked(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;
  try {
    return isDeviceRevokedAnswer(await response.clone().json());
  } catch {
    return false;
  }
}

type Unpair = () => Promise<void> | void;

/** The app's part of unpairing (main.ts): the socket, the lesson, the queue, the screen. */
let unpairApp: Unpair | undefined;

/** One unpair per token: every request and socket that hears it at once shares it. */
const revocations = new Map<string, Promise<void>>();

/** What the app does when its pairing ends, wherever that was heard. `undefined` lets go. */
export function whenRevoked(handler: Unpair | undefined): void {
  unpairApp = handler;
}

/**
 * The pairing has ended: the one place it is acted on, whichever request or
 * socket heard it first -- the check-in, a ticket, a lesson, the hub's
 * `device-revoked`. Once per token, and only for the token this headset holds
 * now: an answer about an earlier school's token, arriving after the headset
 * was paired to the next, changes nothing.
 *
 * The token goes first (synchronously, inside forgetIdentity), then the app's
 * part starts -- closing the socket before anything is awaited, so no lesson
 * or prefetch can be handed out meanwhile -- while the school's caches are
 * deleted. Resolves once both are done.
 */
export function revoked(token: string): Promise<void> {
  const running = revocations.get(token);
  if (running) return running;
  if (storedIdentity()?.deviceToken !== token) return Promise.resolve();

  const done = (async (): Promise<void> => {
    const forgetting = forgetIdentity();
    try {
      await unpairApp?.();
    } catch (error) {
      // The app's part failing must not make the caller think this was anything but an unlink,
      // and the school's data goes regardless.
      console.error('the app could not finish unpairing', error);
    }
    await forgetting;
  })();
  revocations.set(token, done);
  return done;
}

/**
 * A request to our API with the device token. On the API's `device-revoked`
 * the headset is unpaired (revoked) before this throws DeviceRevokedError;
 * any other answer is handed back as it is, and a network failure rejects as
 * fetch does.
 */
export async function deviceFetch(
  identity: DeviceIdentity,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${identity.deviceToken}`);
  const response = await fetch(path, { ...init, headers });
  if (await saysRevoked(response)) {
    await revoked(identity.deviceToken);
    throw new DeviceRevokedError();
  }
  return response;
}

export type CheckInResult =
  /**
   * `deviceId`: its own id, as the furnishing messages name holders (ADR-037); absent from an older
   * API. `platform`: one of the platform's own test headsets (ADR-058), which waits for previews only.
   */
  | { status: 'ok'; name: string; deviceId?: string; platform?: boolean }
  /** The token is no longer accepted: unpaired, or the tenant was removed. */
  | { status: 'revoked' }
  /** The API could not be reached; the headset stays paired and works offline. */
  | { status: 'offline' };

/**
 * Confirms the pairing is still good, and reports in.
 *
 * Called on startup. A revoked headset -- the API's `device-revoked`, and
 * nothing less -- is unpaired (revoked) and sent back to the pairing screen; an
 * unreachable API is not, because a classroom without wifi is ordinary and a
 * headset must still run its cached lesson (docs/02).
 */
export async function checkIn(
  identity: DeviceIdentity,
  report: { appVersion?: string; battery?: number } = {},
): Promise<CheckInResult> {
  const query = new URLSearchParams();
  if (report.appVersion) query.set('appVersion', report.appVersion);
  if (typeof report.battery === 'number') query.set('battery', String(Math.round(report.battery)));

  // Whatever went wrong since the last check-in that got through, or 'none'
  // to say it is fine now. A headset that never reports either leaves an old
  // complaint standing, which is right: support needs "this happened two days
  // ago" to survive until somebody looks.
  const trouble = troubleToReport();
  query.set('lastError', trouble?.code ?? 'none');
  if (trouble?.note !== undefined) query.set('lastErrorNote', trouble.note);

  try {
    const response = await deviceFetch(
      identity,
      `/v1/me/device${query.size ? `?${query.toString()}` : ''}`,
    );

    // Any other refusal -- a bare 401 from a proxy included -- leaves the
    // pairing alone, and is worth a line to support.
    if (!response.ok) {
      rememberTrouble('api-unreachable', `HTTP ${response.status}`);
      return { status: 'offline' };
    }

    const body = (await response.json()) as { name: string; id?: unknown; platform?: unknown };
    // Told, so it can be forgotten. Only after a check-in that succeeded:
    // clearing on the attempt would lose exactly the reports that matter.
    forgetTrouble();
    return {
      status: 'ok',
      name: body.name,
      ...(typeof body.id === 'string' ? { deviceId: body.id } : {}),
      ...(body.platform === true ? { platform: true } : {}),
    };
  } catch (error) {
    // Unlinked: deviceFetch has unpaired it, school data and all (revoked).
    if (error instanceof DeviceRevokedError) return { status: 'revoked' };
    // Offline, not revoked. Losing wifi must never unpair a headset -- but it
    // is worth remembering, because "could not reach the server" is the
    // failure that looks like every other failure from a console.
    rememberTrouble('api-unreachable', (error as Error)?.name);
    return { status: 'offline' };
  }
}

/** Battery level as a percentage, when the browser will say. */
export async function batteryLevel(): Promise<number | undefined> {
  type WithBattery = Navigator & { getBattery?: () => Promise<{ level: number }> };
  const getBattery = (navigator as WithBattery).getBattery;
  if (!getBattery) return undefined;
  try {
    const battery = await getBattery.call(navigator);
    return Math.round(battery.level * 100);
  } catch {
    return undefined;
  }
}

type StartResponse = { code: string; pollToken: string; expiresAt: string };

/**
 * Runs the whole pairing handshake, reporting each step.
 *
 * Polling rather than a socket: pairing happens once per headset, a classroom
 * network drops connections, and a retried GET is far easier to reason about
 * than a reconnecting socket. `signal` stops it when the screen goes away.
 */
export async function pairDevice(
  onProgress: (progress: PairingProgress) => void,
  options: { signal?: AbortSignal; intervalMs?: number } = {},
): Promise<void> {
  const interval = options.intervalMs ?? 2000;

  let started: StartResponse;
  try {
    const response = await fetch('/v1/pairing-codes', {
      method: 'POST',
      signal: options.signal ?? null,
    });
    if (!response.ok) throw new Error(`server said ${response.status}`);
    started = (await response.json()) as StartResponse;
  } catch (error) {
    onProgress({
      status: 'error',
      message: error instanceof Error ? error.message : 'kon geen code ophalen',
    });
    return;
  }

  const expiresAt = new Date(started.expiresAt);
  onProgress({ status: 'code', code: started.code, expiresAt });

  while (!options.signal?.aborted) {
    if (Date.now() >= expiresAt.getTime()) {
      onProgress({ status: 'expired' });
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
    if (options.signal?.aborted) return;

    try {
      const response = await fetch('/v1/pairing-codes/poll', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pollToken: started.pollToken }),
        signal: options.signal ?? null,
      });

      // 202 means the admin has not typed the code yet; keep waiting.
      if (response.status === 202) continue;

      if (response.status === 200) {
        const body = (await response.json()) as { deviceToken: string };
        const identity: DeviceIdentity = {
          deviceToken: body.deviceToken,
          pairedAt: new Date().toISOString(),
        };
        // Whatever an earlier school left -- a token lost to eviction or a
        // cleared site setting can leave its caches behind -- goes before
        // this school's token arrives, so nothing of it is ever served under
        // the new pairing.
        await forgetSchoolData();
        storeIdentity(identity);
        onProgress({ status: 'paired', identity });
        return;
      }

      // 404: the code expired or was already collected. Either way this
      // handshake is over and the headset needs a fresh code.
      onProgress({ status: 'expired' });
      return;
    } catch (error) {
      if (options.signal?.aborted) return;
      // A dropped Wi-Fi mid-pairing is ordinary in a classroom; keep polling
      // until the code actually expires.
      if (error instanceof Error && error.name === 'AbortError') return;
    }
  }
}
